import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ShoppingCart, Package, Plus, Trash2, LogOut,
  CreditCard, MapPin, Clock, CheckCircle, ChevronRight,
  Eye, X, ExternalLink, Loader2, Camera, Upload, Truck, Zap, Coins, Inbox,
  ClipboardList, Store, Briefcase, ShoppingBag as MarketplaceIcon, Copy, Search as SearchIcon,
  LayoutDashboard, Wallet
} from "lucide-react";
import { toast } from "sonner";
import { VirtualCardModal } from "@/pages/linkamazon/VirtualCardModal";
import VipPipCardModal from "./VipPipCardModal";
import { CryptoRechargeModal } from "@/pages/linkamazon/CryptoRechargeModal";
import { ListRequestsStatusModal } from "@/components/marketplace/ListRequestsStatusModal";
import { ListRequestModal } from "@/components/marketplace/ListRequestModal";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

export const LinkAmazonDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVipPipModal, setShowVipPipModal] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [startingPunchout, setStartingPunchout] = useState(false);
  const [punchoutCart, setPunchoutCart] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // States for Marketplace List Feature
  const [meruRequests, setMeruRequests] = useState([]);
  const [cryptoRequests, setCryptoRequests] = useState([]);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [showListStatusModal, setShowListStatusModal] = useState(false);
  const [showListRequest, setShowListRequest] = useState(false);
  const [selectedSellerToList, setSelectedSellerToList] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [showSellerPicker, setShowSellerPicker] = useState(false);
  const [sellerSearchQuery, setSellerSearchQuery] = useState("");

  // New order form
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: "", quantity: 1, unit_price: 0 });
  const [notes, setNotes] = useState("");
  const [shippingAddress, setShippingAddress] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "Haiti",
    phone: ""
  });
  const [shippingOption, setShippingOption] = useState("custom"); // "custom" or "deko"

  // DEKO Shipping Service Address
  const DEKO_ADDRESS = {
    name: "DEKO Shipping Service",
    street: "610 SW 34th St, Suite 101",
    city: "Fort Lauderdale",
    state: "FL",
    zip: "33315",
    country: "USA",
    phone: ""
  };

  const [globalSettings, setGlobalSettings] = useState(null);
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const [selectedShippingCompanyId, setSelectedShippingCompanyId] = useState("");
  const [shippingLbs, setShippingLbs] = useState(0);
  const [payShippingNow, setPayShippingNow] = useState(true);

  const token = localStorage.getItem("buyer_token");

  useEffect(() => {
    const userData = localStorage.getItem("buyer_user");
    if (!token || !userData) {
      navigate("/linkamazon/login");
      return;
    }
    setUser(JSON.parse(userData));
    fetchOrders();
    fetchGlobalSettings();
    fetchShippingCompanies();
    fetchSellers();

    // Check for punchout return
    const punchoutStatus = searchParams.get("punchout");
    const cartCookie = searchParams.get("cart");
    const error = searchParams.get("error");

    if (punchoutStatus || cartCookie || error) {
      console.log("URL Params detected - Status:", punchoutStatus, "Cookie:", cartCookie, "Error:", error);
    }
    const paymentStatus = searchParams.get("payment");
    const paymentReason = searchParams.get("reason");
    const orderNumber = searchParams.get("order");

    if (punchoutStatus === "success" && cartCookie) {
      fetchPunchoutCart(cartCookie);
    } else if (error) {
      toast.error(`Erè Amazon: ${searchParams.get("reason") || error}`);
    }

    // Handle payment callback
    if (paymentStatus === "success") {
      setShowSuccessModal(true);
      toast.success(`Peman reyisi! Kòmand ${orderNumber || ''} peye avèk siksè!`);
      window.history.replaceState({}, '', '/linkamazon/dashboard');
    } else if (paymentStatus === "error") {
      toast.error(`Erè nan peman: ${paymentReason || 'Tanpri eseye ankò'}`);
      window.history.replaceState({}, '', '/linkamazon/dashboard');
    }
  }, [token, navigate, searchParams]);

  // Heartbeat for online tracking
  useEffect(() => {
    if (!token) return;
    const sendHeartbeat = () => {
      axios.post(`${API}/linkamazon/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => { });
    };
    sendHeartbeat(); // Send immediately
    const interval = setInterval(sendHeartbeat, 120000); // Every 2 minutes
    return () => clearInterval(interval);
  }, [token]);

  const fetchGlobalSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchShippingCompanies = async () => {
    try {
      const response = await axios.get(`${API}/shipping`);
      setShippingCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedShippingCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching shipping:", error);
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/sellers`);
      console.log("Sellers fetched:", response.data);
      setSellers(response.data || []);
    } catch (error) {
      console.error("Error fetching sellers:", error);
    }
  };

  const fetchPunchoutCart = async (buyerCookie) => {
    try {
      console.log("Fetching cart for cookie:", buyerCookie);
      const response = await axios.get(`${API}/linkamazon/punchout/cart/${buyerCookie}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("Cart response data:", response.data);

      if (response.data.cart && response.data.cart.items && response.data.cart.items.length > 0) {
        // Convert punchout items to order items
        const cartItems = response.data.cart.items.map(item => ({
          name: item.name || item.asin || "Pwodwi Amazon",
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          tax: item.tax || 0,
          shipping: item.shipping || 0,
          total: item.total || (item.quantity || 1) * (item.unit_price || 0),
          asin: item.asin
        }));

        setItems(cartItems);
        setPunchoutCart(response.data.cart);
        setShowNewOrder(true);
        toast.success("Panye Amazon enpòte avèk siksè!");
      } else {
        toast.error("Panye Amazon an vid oswa done yo pa kòrèk.");
        console.warn("No items in cart or invalid data:", response.data);
      }

      // Always clear URL params to avoid re-triggering
      window.history.replaceState({}, '', '/linkamazon/dashboard');
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast.error("Erè nan jwenn panye a");
    }
  };

  const handleStartPunchout = async () => {
    if (startingPunchout) return;
    setStartingPunchout(true);

    // Clear existing order state before starting new session
    setItems([]);
    setShowNewOrder(false);
    setPunchoutCart(null);

    try {
      console.log("Starting punchout session...");
      const response = await axios.post(`${API}/linkamazon/punchout/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.redirect_url) {
        toast.success("Ap redirije ou sou Amazon Business...");
        window.location.href = response.data.redirect_url;
      } else {
        toast.error("Pa jwenn lyen Amazon");
      }
    } catch (error) {
      console.error("Punchout error:", error);
      const errorMsg = error.response?.data?.detail || "Erè nan koneksyon Amazon";
      toast.error(errorMsg);
    } finally {
      setStartingPunchout(false);
    }
  };

  const [productOrders, setProductOrders] = useState([]);

  const fetchOrders = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentPhone = localStorage.getItem("buyer_phone") || user?.phone;
      const [ordersRes, meruRes, cryptoRes, marketplaceRes, giftCardsRes, productOrdersRes] = await Promise.all([
        axios.get(`${API}/linkamazon/orders`, { headers }),
        axios.get(`${API}/linkamazon/meru-requests`, { headers }),
        axios.get(`${API}/linkamazon/crypto-requests`, { headers }),
        axios.get(`${API}/marketplace/list-requests/my-requests?phone=${currentPhone}`, { headers }),
        axios.get(`${API}/my-gift-cards?phone=${currentPhone}`, { headers }),
        axios.get(`${API}/marketplace/buyer/orders?phone=${currentPhone}`),
      ]);
      setOrders(ordersRes.data.orders || []);
      setMeruRequests(meruRes.data.requests || []);
      setCryptoRequests(cryptoRes.data.requests || []);
      setMarketplaceOrders(marketplaceRes.data || []);
      setGiftCards(giftCardsRes.data.orders || []);
      setProductOrders(productOrdersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBid = async (orderId, driverId, amount) => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/marketplace/orders/${orderId}/accept-bid`, {
        driver_id: driverId,
        amount: amount
      });
      toast.success("Ou aksepte pri livrezon sa! ✅");
      fetchOrders();
    } catch (error) {
      toast.error("Erè nan aksepte pri a.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayMarketplaceOrder = async (orderId) => {
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/marketplace/orders/${orderId}/pay-moncash`);
      if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      }
    } catch (error) {
      toast.error("Erè nan prepare peman an.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("buyer_token");
    localStorage.removeItem("buyer_user");
    navigate("/linkamazon/login");
  };

  const addItem = () => {
    if (!newItem.name || newItem.unit_price <= 0) {
      toast.error("Tanpri ranpli non ak pri atik la");
      return;
    }
    const item = {
      ...newItem,
      total: newItem.quantity * newItem.unit_price
    };
    setItems([...items, item]);
    setNewItem({ name: "", quantity: 1, unit_price: 0 });
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const getTotal = () => {
    const amazon_base = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const amazon_tax = items.reduce((sum, item) => sum + (item.tax || 0), 0);
    const amazon_shipping = items.reduce((sum, item) => sum + (item.shipping || 0), 0);
    const amazon_subtotal = amazon_base + amazon_tax + amazon_shipping;

    // Fee (default 10%)
    const fee_percent = globalSettings?.bvglinkamazon_fee_percent || 0.10;
    const fee = amazon_subtotal * fee_percent;

    const base_total_usd = amazon_subtotal + fee;

    // Haiti Shipping
    let haiti_shipping_fee = 0;
    const selectedCompany = shippingCompanies.find(c => c.id === selectedShippingCompanyId);
    if (selectedCompany && shippingLbs > 0) {
      haiti_shipping_fee = selectedCompany.price_per_lbs * shippingLbs;
    }

    const total_usd = payShippingNow ? (base_total_usd + haiti_shipping_fee) : base_total_usd;
    const rate = globalSettings?.usd_to_htg_rate || 148;
    const total_htg = total_usd * rate;

    return {
      amazon_base, amazon_tax, amazon_shipping, amazon_subtotal,
      fee, fee_percent, base_total_usd, haiti_shipping_fee,
      total: total_usd, total_htg, exchange_rate: rate
    };
  };

  const handleSubmitOrder = async () => {
    if (items.length === 0) {
      toast.error("Ajoute omwen yon atik");
      return;
    }

    // Get the correct shipping address based on option
    const finalShippingAddress = shippingOption === "deko" ? DEKO_ADDRESS : shippingAddress;

    if (shippingOption === "custom" && (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city)) {
      toast.error("Ranpli adrès livrezon an (Non, Ri, Vil)");
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API}/linkamazon/orders`,
        {
          items,
          notes,
          shipping_address: finalShippingAddress,
          shipping_company_id: selectedShippingCompanyId,
          shipping_lbs: parseFloat(shippingLbs),
          pay_shipping_now: payShippingNow,
          shipping_service: shippingOption === "deko" ? "DEKO" : "CUSTOM"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Kòmand kreye avèk siksè!");
        setShowNewOrder(false);
        setItems([]);
        setNotes("");
        setShippingOption("custom");
        setShippingAddress({
          name: "",
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "Haiti",
          phone: ""
        });
        fetchOrders();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(error.response?.data?.detail || "Erè nan kreye kòmand");
    } finally {
      setSubmitting(false);
    }
  };

  const [paymentInstructions, setPaymentInstructions] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Èske ou sèten ou vle efase kòmand sa a?")) {
      return;
    }

    setDeletingOrder(orderId);
    try {
      const response = await axios.delete(
        `${API}/linkamazon/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Kòmand efase avèk siksè");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Erè nan efase kòmand");
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleDeleteListRequest = async (e, requestId) => {
    e.stopPropagation();
    if (!window.confirm("Èske w sèten ou vle efase demann sa a nèt? Ou p'ap ka wè l ankò.")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/marketplace/list-requests/${requestId}`);
      if (response.data.success) {
        toast.success("Demann lan efase nèt.");
        fetchOrders();
      }
    } catch (error) {
      console.error("Delete list request error:", error);
      toast.error("Erè nan efase demann lan.");
    }
  };

  const handlePayment = async (orderId, method) => {
    try {
      const response = await axios.post(
        `${API}/linkamazon/orders/${orderId}/pay`,
        { payment_method: method },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.redirect_url) {
        // MonCash redirect
        window.location.href = response.data.redirect_url;
      } else if (response.data.show_instructions) {
        // Show payment instructions modal
        setPaymentInstructions({
          id: orderId,
          method: response.data.payment_method,
          amount: response.data.amount_htg || response.data.amount_usd,
          currency: response.data.amount_htg ? "HTG" : "USD",
          instructions: response.data.instructions,
          message: response.data.message,
          orderNumber: showPayment?.order_number
        });
        setShowPayment(null);
        fetchOrders();
      } else {
        toast.success(response.data.message || "Peman inisye");
        setShowPayment(null);
        fetchOrders();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Erè nan peman");
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);
          resolve(compressedBase64);
        };
      };
    });
  };

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingProof(true);
      try {
        const compressed = await compressImage(file);
        setProofPreview(compressed);
      } catch (err) {
        console.error("Compression error:", err);
        toast.error("Erè nan prepare foto a");
      } finally {
        setUploadingProof(false);
      }
    }
  };

  const handleUploadProof = async (orderId) => {
    if (!proofPreview) return;

    console.log("DEBUG: handleUploadProof called with orderId:", orderId);
    if (!orderId || orderId === "undefined" || orderId === "[object Object]") {
      const msg = `Manke ID kòmand (ID resevwa: ${orderId})`;
      console.error(msg);
      toast.error(msg, { duration: 10000 });
      return;
    }

    setUploadingProof(true);

    const baseUrl = API.replace(/\/+$/, "");
    const uploadUrl = `${baseUrl}/linkamazon/orders/${orderId}/proof`;

    try {
      const response = await axios.post(
        uploadUrl,
        { proof_image: proofPreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setPaymentInstructions(null);
        setProofPreview(null);
        fetchOrders();
      }
    } catch (error) {
      console.error("Proof upload error:", error);
      toast.error(error.response?.data?.detail || "Erè nan voye prèv la.");
    } finally {
      setUploadingProof(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_approval: { bg: "bg-yellow-100 text-yellow-800", label: "Prè pou Peye" },
      approved: { bg: "bg-green-100 text-green-800", label: "Apwouve" },
      pending_payment: { bg: "bg-orange-100 text-orange-800", label: "Ap Tann Peman" },
      pending_payment_verification: { bg: "bg-purple-100 text-purple-800", label: "Ap Verifye Peman" },
      paid: { bg: "bg-blue-100 text-blue-800", label: "Peye" },
      processing: { bg: "bg-indigo-100 text-indigo-800", label: "Ap Trete" },
      transmitted: { bg: "bg-cyan-100 text-cyan-800", label: "Voye sou Amazon" },
      shipped: { bg: "bg-teal-100 text-teal-800", label: "Ekspedye" },
      completed: { bg: "bg-green-100 text-green-800", label: "Konplè" },
      rejected: { bg: "bg-red-100 text-red-800", label: "Rejte" },
      cancelled: { bg: "bg-gray-100 text-gray-800", label: "Anile" }
    };
    const c = config[status] || { bg: "bg-gray-100 text-gray-800", label: status?.replace(/_/g, ' ') || 'Enkoni' };
    return <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${c.bg}`}>{c.label}</span>;
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return ['pending_approval', 'approved', 'pending_payment', 'pending_payment_verification'].includes(order.status);
    if (activeTab === 'paid') return ['paid', 'processing', 'transmitted', 'shipped'].includes(order.status);
    if (activeTab === 'completed') return ['completed'].includes(order.status);
    return true;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending_approval: '#EAB308',
      approved: '#22C55E',
      pending_payment: '#F97316',
      pending_payment_verification: '#A855F7',
      paid: '#3B82F6',
      processing: '#6366F1',
      transmitted: '#06B6D4',
      shipped: '#14B8A6',
      completed: '#22C55E',
      rejected: '#EF4444',
      cancelled: '#9CA3AF'
    };
    return colors[status] || '#9CA3AF';
  };

  const getProgressPercentage = (status) => {
    const weights = {
      pending_approval: 10,
      approved: 20,
      pending_payment: 30,
      pending_payment_verification: 40,
      paid: 55,
      processing: 70,
      transmitted: 80,
      shipped: 90,
      completed: 100,
      rejected: 0,
      cancelled: 0,
      // Marketplace Product Statuses
      pending: 10,
      accepted_bid: 40,
      accepted: 40,
      bidding: 25
    };
    
    // Special check for payment_status if it's a marketplace order object
    if (status === 'completed' || status === 'paid') return 100;
    
    return weights[status] || 5;
  };

  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Jodi a';
    if (diffDays === 1) return 'Yè';
    if (diffDays < 7) return `${diffDays} jou de sa`;
    return date.toLocaleDateString('ht-HT');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9900]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EAEDED' }}>
      <nav className="bg-[#1E293B] text-white p-5 sticky top-0 z-[100] shadow-2xl">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 md:gap-8">
             <div className="flex flex-col">
               <a href="https://www.bvglinkamazon.com" className="text-xl md:text-2xl font-black italic tracking-tighter uppercase leading-none hover:text-[#FF9900] transition-colors">
                 Link Amazon
               </a>
               <p className="text-[8px] font-black tracking-[0.2em] text-[#FF9900] uppercase">Dashboard</p>
             </div>
             
             {/* OBVIOUS RETURN BUTTON */}
             <a 
                href="/bvgpay/wallet" 
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-2xl text-[10px] sm:text-xs font-black uppercase transition-all duration-500 shadow-xl shadow-indigo-900/20"
             >
                <Wallet className="w-3.5 h-3.5" />
                <span>Bous Mwen</span>
             </a>
             <a 
                href="https://www.bvglinkamazon.com" 
                className="flex items-center gap-2 bg-[#FF9900] hover:bg-black text-black hover:text-white px-5 py-2.5 rounded-2xl text-[10px] sm:text-xs font-black uppercase transition-all duration-500 shadow-xl shadow-orange-900/20"
             >
                <Store className="w-3.5 h-3.5" />
                <span>Retounen sou Site</span>
             </a>
          </div>

          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md px-3 py-1.5 rounded-2xl border border-white/5">
                <div className="w-8 h-8 rounded-xl bg-[#FF9900] flex items-center justify-center text-black font-black text-xs shadow-lg">
                   {(user?.name || user?.email || '?')[0].toUpperCase()}
                </div>
                <span className="text-xs font-bold text-gray-200 hidden md:inline">{user?.name || user?.email}</span>
             </div>
             <Button 
                variant="ghost" 
                onClick={handleLogout} 
                className="text-gray-400 hover:text-red-500 rounded-2xl h-11 w-11 p-0 hover:bg-red-500/10 transition-all"
             >
                <LogOut className="w-5 h-5" />
             </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Top Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Amazon</p>
             <p className="text-3xl font-black text-[#232F3E]">{orders.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" onClick={() => setActiveTab('marketplace')} style={{cursor: 'pointer'}}>
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Marketplace</p>
             <p className="text-3xl font-black text-[#FF9900]">{marketplaceOrders.length + productOrders.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Sèvis</p>
             <p className="text-3xl font-black text-blue-600">{meruRequests.length + cryptoRequests.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Kat</p>
             <p className="text-3xl font-black text-indigo-600">{giftCards.length}</p>
          </div>
        </div>

         {/* Marketplace Banner */}
         <div className="bg-[#232F3E] rounded-3xl p-8 shadow-2xl relative overflow-hidden group border-b-4 border-[#FF9900]">
            <div className="absolute right-0 top-0 w-64 h-64 bg-white/5 rounded-full translate-x-20 -translate-y-20 group-hover:scale-110 transition-all duration-1000" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-10">
               <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#FF9900] to-[#FFB347] rounded-3xl flex items-center justify-center text-black shadow-2xl">
                     <Store className="w-10 h-10" />
                  </div>
                  <div>
                     <h3 className="text-2xl md:text-3xl font-black text-white italic tracking-tighter uppercase leading-none mb-2">Marketplace Lokal</h3>
                     <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-widest max-w-sm mb-4">Jwenn pwodwi bò lakay ou rapid. Voye lis ou n ap ba w pri.</p>
                     
                     <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto items-center">
                        <Button onClick={() => navigate('/marketplace')} className="bg-black/50 hover:bg-black text-white border border-white/20 font-bold h-10 px-6 rounded-full shadow-lg uppercase text-[10px] transition-all flex items-center gap-2 mb-2 sm:mb-0 w-full sm:w-auto justify-center">
                           <MarketplaceIcon className="w-3.5 h-3.5" /> Al nan Market
                        </Button>
                        <div className="flex flex-wrap gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                           <Button onClick={() => setShowSellerPicker(true)} className="bg-[#FF9900] hover:bg-[#E88B00] text-black font-black h-12 px-6 rounded-2xl shadow-xl uppercase tracking-widest text-xs flex-1 min-w-[140px]">
                              <ClipboardList className="w-5 h-5 mr-2" /> Voye yon Lis
                           </Button>
                           <Button onClick={() => navigate('/transport')} className="bg-green-500 hover:bg-green-600 text-white font-black h-12 px-6 rounded-2xl shadow-xl uppercase tracking-widest text-xs flex-1 min-w-[140px]">
                              <Truck className="w-5 h-5 mr-2" /> Kòmande Transpò
                           </Button>
                           <Button onClick={() => setShowListStatusModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-black h-12 px-6 rounded-2xl shadow-xl uppercase tracking-widest text-xs relative overflow-visible flex-1 min-w-[140px]">
                              <CreditCard className="w-5 h-5 mr-2" /> Ordres Lis
                              {marketplaceOrders.some(m => m.status === 'quoted') && (
                                 <span className="absolute -top-1 -right-1 flex h-5 w-5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white items-center justify-center text-[8px] font-black">
                                       {marketplaceOrders.filter(m => m.status === 'quoted').length}
                                    </span>
                                 </span>
                              )}
                           </Button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {/* BVG LINK POS ONLINE Banner */}
         <div className="bg-[#6366F1] rounded-3xl p-8 shadow-2xl relative overflow-hidden group border-b-4 border-white/20">
            <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full translate-x-20 -translate-y-20 group-hover:scale-110 transition-all duration-1000" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-10 text-white">
               <div className="flex items-center gap-6 text-left">
                  <div className="w-20 h-20 bg-white text-[#6366F1] rounded-3xl flex items-center justify-center shadow-2xl">
                     <LayoutDashboard className="w-10 h-10" />
                  </div>
                  <div>
                     <h3 className="text-2xl md:text-3xl font-black italic tracking-tighter uppercase leading-none mb-2">BVG LINK POS ONLINE</h3>
                     <p className="text-white/80 text-xs md:text-sm font-bold uppercase tracking-widest max-w-sm mb-4">Achte dirèkteman nan magazen yo. Chwazi sa w bezwen, peye rapid.</p>
                     
                     <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto items-center">
                        <Button 
                           onClick={() => navigate('/pos')} 
                           className="bg-white text-[#6366F1] hover:bg-[#EAEDED] font-black h-12 px-10 rounded-2xl shadow-xl uppercase tracking-widest text-xs flex items-center gap-3 transition-transform hover:scale-105"
                        >
                           <Zap className="w-5 h-5" /> AL NAN POS
                        </Button>
                     </div>
                  </div>
               </div>
            </div>
         </div>

        {/* Action Cards */}
        <div className="space-y-4">
           {/* LINKLI CARDS */}
           <div className="bg-[#0b1c3b] rounded-2xl p-6 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 text-white border border-[#2ccb00]/30">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                 <div className="bg-white/10 p-3 rounded-full flex items-center justify-center">
                    <CreditCard className="w-8 h-8 text-[#2ccb00]" />
                 </div>
                 <div>
                    <img src="/logo-linkcards.png" alt="LINKLI CARDS" className="h-10 mb-2 object-contain" />
                    <ul className="text-white/80 text-xs list-disc ml-4 space-y-1">
                       <li>Kreye kat vityèl (Visa/Mastercard)</li>
                       <li>Peye sou entènèt (Amazon, Netflix, elatriye)</li>
                       <li>Recharge kat la ak MonCash</li>
                    </ul>
                 </div>
              </div>
              <a href="/linklicards" target="_blank" rel="noreferrer" className="bg-gradient-to-r from-[#2ccb00] to-[#1e8a00] hover:from-[#35f000] hover:to-[#22a100] text-white shadow-xl px-10 h-14 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center transition-all hover:scale-105">
                 KREYE / RECHAJE
              </a>
           </div>
        </div>

        {/* Tab Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {['all', 'marketplace', 'services', 'cards'].map(key => (
            <button key={key} onClick={() => setActiveTab(key)} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === key ? 'bg-[#232F3E] text-white shadow-lg' : 'bg-white text-gray-500 border'}`}>
              {key === 'all' ? 'Amazon' : key === 'marketplace' ? 'Marketplace' : key === 'services' ? 'Sèvis' : 'Kat'}
            </button>
          ))}
        </div>

        {/* List Content */}
        <div className="space-y-4">
          {activeTab === 'marketplace' ? (
             <div className="space-y-8">
               {/* List Requests Section */}
               <div className="space-y-4">
                  <h3 className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-4">Demann Lis (Quotes)</h3>
                  {marketplaceOrders.length === 0 ? <p className="text-center py-6 text-xs text-gray-400">Ou poko gen okenn lis.</p> : marketplaceOrders.map(req => (
                    <div key={req.id} onClick={() => setShowListStatusModal(true)} className="bg-white rounded-[32px] p-6 border shadow-sm flex flex-col md:flex-row justify-between items-center gap-4 cursor-pointer hover:shadow-md transition-all">
                       <div className="flex-1 w-full md:w-auto">
                         <div className="flex items-center gap-5 mb-3">
                           <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center shrink-0"><Store className="text-[#FF9900]" /></div>
                           <div>
                             <h4 className="font-black text-[#232F3E] text-lg uppercase tracking-tighter italic">Magazen: {req.seller_name || 'Enkoni'}</h4>
                             <p className="text-[10px] text-gray-400 font-bold uppercase">{req.status} • {formatRelativeDate(req.created_at)}</p>
                           </div>
                         </div>
                         {/* Visual Progress Bar */}
                         <div className="px-1 py-1 w-full md:max-w-md">
                            <div className="flex justify-between items-center mb-1">
                               <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">PWOGRÈ</span>
                               <span className="text-xs font-black text-[#FF9900] italic">{getProgressPercentage(req.status)}%</span>
                            </div>
                            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200/50 p-0.5">
                               <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-sm" style={{ width: `${getProgressPercentage(req.status)}%` }}></div>
                            </div>
                         </div>
                       </div>
                       <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className={`px-5 py-2 rounded-full text-[10px] font-black uppercase ${req.status === 'quoted' ? 'bg-green-100 text-green-700' : 'bg-indigo-50 text-indigo-700'}`}>{req.status}</span>
                          {req.status === 'quoted' && <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowListStatusModal(true); }} className="bg-green-600 hover:bg-black text-white rounded-xl h-9 px-6 font-black uppercase text-[10px]">Peye Kounye a</Button>}
                          {(req.status === 'pending' || req.status === 'cancelled') && (
                            <Button size="sm" variant="ghost" onClick={(e) => handleDeleteListRequest(e, req.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl h-8 px-4 font-black uppercase text-[9px] mt-1">Efase Nèt</Button>
                          )}
                          <p className="text-2xl font-black italic">{req.total_htg?.toLocaleString() || 0} HTG</p>
                       </div>
                    </div>
                  ))}
               </div>

               {/* Product Orders Section (New) */}
               <div className="space-y-4">
                  <h3 className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-4">Kòmand Pwodwi (Marketplace)</h3>
                  {productOrders.length === 0 ? <p className="text-center py-10 text-xs text-gray-400">Pa gen kòmand pwodwi kounye a.</p> : productOrders.map(order => (
                    <div key={order.id} className="bg-white rounded-[32px] p-6 border shadow-sm space-y-6">
                       <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                          <div className="flex items-center gap-5">
                            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center"><MarketplaceIcon className="text-blue-600" /></div>
                            <div>
                              <h4 className="font-black text-[#232F3E] text-lg uppercase tracking-tighter italic">{order.product_title}</h4>
                              <p className="text-[10px] text-gray-400 font-bold">Kòmand: {order.id.slice(0,8)} • {formatRelativeDate(order.created_at)}</p>
                            </div>
                          </div>

                          {/* Visual Progress Bar */}
                          <div className="px-1 py-1">
                             <div className="flex justify-between items-center mb-2">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">PWOGRÈ KÒMAND LAN</span>
                                <span className="text-xs font-black text-[#FF9900] italic">{getProgressPercentage(order.payment_status === 'completed' ? 'completed' : (order.delivery_info?.status || 'pending'))}%</span>
                             </div>
                             <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200/50 p-0.5">
                                <div className="h-full bg-gradient-to-r from-[#FF9900] to-[#FFB347] rounded-full transition-all duration-1000 shadow-sm" style={{ width: `${getProgressPercentage(order.payment_status === 'completed' ? 'completed' : (order.delivery_info?.status || 'pending'))}%` }}></div>
                             </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                             <span className={`px-5 py-2 rounded-full text-[10px] font-black uppercase ${order.payment_status === 'completed' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                {order.payment_status === 'completed' ? 'Peye' : 'Atant Peman'}
                             </span>
                             <p className="text-2xl font-black italic tracking-tighter">{(order.price_htg + (order.delivery_info?.fee || 0)).toLocaleString()} HTG</p>
                          </div>
                       </div>

                       {/* Delivery Bidding Section */}
                       {order.delivery_info?.type === 'delivery' && order.payment_status !== 'completed' && (
                         <div className="bg-gray-50 rounded-2xl p-6 space-y-4 border border-gray-100">
                           <div className="flex justify-between items-center">
                              <div>
                                 <p className="text-[10px] font-black uppercase text-[#FF9900]">Statistik Livrezon</p>
                                 <p className="font-bold text-sm italic">{order.delivery_info.status === 'bidding' ? 'N\'ap tann chofè ba w propozisyon...' : 'Livrezon aksepte!'}</p>
                              </div>
                              <div className="text-right">
                                 <p className="text-[10px] font-black uppercase text-gray-400">Pri Livrezon</p>
                                 <p className="text-lg font-black text-blue-600">{order.delivery_info.fee?.toLocaleString()} HTG</p>
                              </div>
                           </div>

                           {/* Bids List */}
                           {order.delivery_info.status === 'bidding' && order.delivery_info.bids && order.delivery_info.bids.length > 0 && (
                             <div className="space-y-4 pt-4 border-t border-gray-200">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Pwozpizisyon Chofè yo:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                   {order.delivery_info.bids.map((bid, i) => (
                                     <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 flex justify-between items-center shadow-sm">
                                        <div>
                                           <p className="font-black text-sm uppercase">{bid.driver_name}</p>
                                           <p className="text-xl font-black text-green-600 italic">{bid.amount.toLocaleString()} HTG</p>
                                        </div>
                                        <Button 
                                           onClick={() => handleAcceptBid(order.id, bid.driver_id, bid.amount)}
                                           size="sm" 
                                           className="bg-blue-600 hover:bg-black font-black uppercase text-[10px] h-9"
                                        >
                                           AKSEPTE
                                        </Button>
                                     </div>
                                   ))}
                                </div>
                             </div>
                           )}

                           {/* Final Payment Trigger */}
                           {order.delivery_info.status === 'accepted_bid' && (
                             <div className="pt-4 border-t border-gray-200 space-y-4">
                                <div className="p-4 bg-green-50 rounded-xl border border-green-100 flex items-center gap-3">
                                   <CheckCircle className="text-green-500 w-5 h-5" />
                                   <p className="text-xs font-bold text-green-800">Ou aksepte pri nòmal la! Ou mèt pase nan peman kounye a.</p>
                                </div>
                                <Button 
                                   onClick={() => handlePayMarketplaceOrder(order.id)}
                                   className="w-full bg-[#E31937] hover:bg-black text-white h-14 font-black uppercase italic tracking-widest text-xs rounded-xl shadow-xl"
                                >
                                   PEYE TOTAL LA AK MONCASH
                                </Button>
                             </div>
                           )}
                         </div>
                       )}

                       {/* Simple Pay for Pickup or already accepted bid */}
                       {order.delivery_info?.type === 'pickup' && order.payment_status === 'pending' && (
                         <Button 
                            onClick={() => handlePayMarketplaceOrder(order.id)}
                            className="w-full bg-[#E31937] hover:bg-black text-white h-14 font-black uppercase italic tracking-widest text-xs rounded-xl shadow-xl"
                         >
                            PEYE KOUNYE A AK MONCASH
                         </Button>
                       )}
                    </div>
                  ))}
               </div>
             </div>
          ) : activeTab === 'services' ? (
             [...meruRequests, ...cryptoRequests].length === 0 ? <p className="text-center py-20 text-gray-400">Pa gen demann sèvis.</p> : [...meruRequests, ...cryptoRequests].map(req => (
               <div key={req.id} className="bg-white rounded-3xl p-6 border flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">{req.method === 'meru' ? <Coins /> : <Zap />}</div>
                    <div><p className="font-black uppercase text-sm">{req.method === 'meru' ? 'Rechaaj Meru' : 'Rechaaj Crypto'}</p><p className="text-[10px] text-gray-400 font-bold uppercase">{formatRelativeDate(req.created_at)} • {req.status}</p></div>
                  </div>
                  <p className="text-xl font-black">${req.amount_usd}</p>
               </div>
             ))
          ) : activeTab === 'cards' ? (
             giftCards.length === 0 ? <p className="text-center py-20 text-gray-400">Pa gen kat kado.</p> : giftCards.map(card => (
               <div key={card.id} className="bg-white rounded-3xl p-6 border flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><CreditCard /></div>
                    <div><p className="font-black uppercase text-sm">{card.brand}</p><p className="text-xs font-bold text-green-600 font-mono italic">{card.claim_code}</p></div>
                  </div>
                  <p className="text-xl font-black">${card.amount_usd}</p>
               </div>
             ))
          ) : filteredOrders.map(order => (
            <div key={order.id} className="bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-center gap-4">
               <div className="flex items-center gap-4 w-full md:w-auto">
                 <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-50 text-gray-400"><Package /></div>
                  <div className="flex-1 min-w-0"><p className="font-bold truncate">{order.order_number}</p><p className="text-xs text-gray-400">{formatRelativeDate(order.created_at)}</p></div>
                </div>

                {/* Progress Bar */}
                <div className="flex-1 hidden md:block px-4">
                   <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-100">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-1000" style={{ width: `${getProgressPercentage(order.status)}%` }}></div>
                   </div>
                   <p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mt-1">Pwogrè: {getProgressPercentage(order.status)}%</p>
                </div>
               <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                 {getStatusBadge(order.status)}
                 <div className="text-right">
                    <p className="text-lg font-black">${order.subtotal?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">{order.total_htg?.toLocaleString()} HTG</p>
                 </div>
                 <div className="flex gap-2">
                    {(order.status === 'pending_approval' || order.status === 'approved' || order.status === 'pending_payment') && (
                      <Button size="sm" onClick={() => setShowPayment(order)} className="bg-green-500 rounded-xl"><CreditCard className="w-3.5 h-3.5 mr-1" /> Peye</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setSelectedOrder(order)} className="rounded-xl"><Eye className="w-3.5 h-3.5" /></Button>
                 </div>
               </div>
            </div>
          ))}
        </div>
      </main>

      {/* Modals */}
      {showNewOrder && (
          <div className="fixed inset-0 bg-black/50 p-4 z-50 flex items-center justify-center overflow-y-auto">
             <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <CardHeader className="flex flex-row justify-between items-center"><CardTitle>Konfime Kòmand Amazon</CardTitle><Button variant="ghost" onClick={() => setShowNewOrder(false)}><X /></Button></CardHeader>
                <CardContent className="space-y-6">
                   <div className="border rounded-lg divide-y">{items.map((item, i) => <div key={i} className="p-3 flex justify-between"><span>{item.name} x{item.quantity}</span><span className="font-bold">${item.total.toFixed(2)}</span></div>)}</div>
                   <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between font-black text-lg"><span>Total USD:</span><span className="text-[#FF9900]">${getTotal().total.toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold"><span>Total HTG:</span><span>{getTotal().total_htg.toLocaleString()} HTG</span></div>
                   </div>
                   <Button onClick={handleSubmitOrder} disabled={submitting} className="w-full bg-[#FF9900] h-14 font-black">KONFIME KÒMAND</Button>
                </CardContent>
             </Card>
          </div>
      )}

      {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 p-4 z-50 flex items-center justify-center"><Card className="w-full max-w-lg"><CardHeader className="flex flex-row justify-between"><CardTitle>{selectedOrder.order_number}</CardTitle><Button variant="ghost" onClick={() => setSelectedOrder(null)}><X /></Button></CardHeader><CardContent className="space-y-4">
            <div className="space-y-2">{selectedOrder.items?.map((item, i) => <div key={i} className="flex justify-between text-sm bg-gray-50 p-2 rounded"><span>{item.name}</span><span>${item.total.toFixed(2)}</span></div>)}</div>
            <div className="border-t pt-4"><div className="flex justify-between font-bold text-lg"><span>Total:</span><span className="text-[#FF9900]">${selectedOrder.subtotal.toFixed(2)}</span></div></div>
            <Button onClick={() => handleDeleteOrder(selectedOrder.id)} disabled={deletingOrder === selectedOrder.id} variant="outline" className="w-full text-red-500 border-red-200"><Trash2 className="w-4 h-4 mr-2" /> Efase Kòmand</Button>
          </CardContent></Card></div>
      )}

      {showPayment && (
          <div className="fixed inset-0 bg-black/50 p-4 z-50 flex items-center justify-center"><Card className="w-full max-w-md"><CardHeader className="flex flex-row justify-between"><CardTitle>Peye Kòmand</CardTitle><Button variant="ghost" onClick={() => setShowPayment(null)}><X /></Button></CardHeader><CardContent className="space-y-4">
             <div className="text-center p-6 bg-gray-50 rounded-2xl"><p className="text-3xl font-black text-[#FF9900]">{showPayment.total_htg?.toLocaleString()} HTG</p></div>
             <Button onClick={() => handlePayment(showPayment.id, "moncash")} className="w-full bg-[#E31937] h-14 font-black text-white">MONCASH</Button>
             <Button onClick={() => handlePayment(showPayment.id, "zelle")} className="w-full bg-[#6D1ED4] h-14 font-black text-white">ZELLE</Button>
          </CardContent></Card></div>
      )}

      {paymentInstructions && (
          <div className="fixed inset-0 bg-black/50 p-4 z-50 flex items-center justify-center"><Card className="w-full max-w-md"><CardHeader className="flex flex-row justify-between"><CardTitle>Enstriksyon Peman</CardTitle><Button variant="ghost" onClick={() => setPaymentInstructions(null)}><X /></Button></CardHeader><CardContent className="space-y-4">
             <div className="p-4 bg-gray-50 rounded-xl text-center font-black text-2xl text-[#FF9900]">{paymentInstructions.amount.toLocaleString()} {paymentInstructions.currency}</div>
             {paymentInstructions.method === "zelle" && <div className="p-3 bg-purple-50 rounded-lg"><p className="font-bold">Zelle: {paymentInstructions.instructions.recipient || paymentInstructions.instructions.email}</p><p className="text-xs">{paymentInstructions.instructions.note}</p></div>}
             <div className="border-t pt-4 space-y-3">
                {!proofPreview ? <label className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center cursor-pointer"><Camera className="mb-2 text-gray-400" /><span>Voye Prèv Peman</span><input type="file" className="hidden" onChange={onFileChange} /></label> : <div className="relative rounded-lg overflow-hidden border"><img src={proofPreview} className="w-full h-48 object-cover" /><Button onClick={() => handleUploadProof(paymentInstructions.id)} className="w-full mt-2 bg-[#FF9900]">VOYE POTO SA A</Button></div>}
             </div>
             <Button variant="outline" className="w-full" onClick={() => setPaymentInstructions(null)}>FÈMEN</Button>
          </CardContent></Card></div>
      )}

      {showSellerPicker && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4">
             <div className="w-full max-w-xl">
                <div className="flex justify-between items-center mb-4 text-white"><h2 className="text-xl font-black uppercase">Chwazi yon <span className="text-[#FF9900]">Magazen</span></h2><Button variant="ghost" onClick={() => setShowSellerPicker(false)} className="text-white"><X /></Button></div>
                <div className="bg-white rounded-[40px] overflow-hidden shadow-2xl p-6 space-y-3">
                   <input type="text" placeholder="Chèche..." className="w-full h-12 border-2 rounded-2xl px-4" value={sellerSearchQuery} onChange={(e) => setSellerSearchQuery(e.target.value)} />
                   <div className="max-h-[450px] overflow-y-auto space-y-2">
                      {sellers.filter(s => (s.store_name || s.fullname || "").toLowerCase().includes(sellerSearchQuery.toLowerCase())).map(s => (
                        <div key={s.id} onClick={() => { setSelectedSellerToList(s); setShowSellerPicker(false); setShowListRequest(true); }} className="flex items-center justify-between p-5 border rounded-[28px] cursor-pointer hover:bg-orange-50">
                           <div className="flex items-center gap-5"><div className="w-14 h-14 bg-[#232F3E] rounded-2xl flex items-center justify-center text-[#FF9900] font-black">{(s.store_name || s.fullname || '?')[0]}</div><h4 className="font-black text-[#232F3E] uppercase">{s.store_name || s.fullname}</h4></div>
                           <ChevronRight />
                        </div>
                      ))}
                   </div>
                </div>
             </div>
          </div>
      )}

      {showListRequest && selectedSellerToList && (
          <ListRequestModal seller={selectedSellerToList} onClose={() => { setShowListRequest(false); fetchOrders(); }} />
      )}
      {showListStatusModal && (
          <ListRequestsStatusModal onClose={() => setShowListStatusModal(false)} onUpdate={fetchOrders} />
      )}
      
      <VirtualCardModal isOpen={searchParams.get("showVirtualCard") === "true"} onClose={() => setSearchParams({})} />
      
      <CryptoRechargeModal isOpen={searchParams.get("showCryptoModal") === "true"} onClose={() => setSearchParams({})} />
      
    </div>
  );
};





